// src/utils/activityProgress.js
// Vocabulário único do progresso de uma atividade.
//
// Antes cada tela mantinha a sua cópia da lista (ActivityForm, ActivityGeneralForm
// e EditActivityForm tinham três), o AddActivityForm trazia o texto escrito à mão
// e o ActivityCardModal montava o rótulo com toTitleCase — o que produzia nomes
// diferentes para o mesmo estado ("Concluída Para Correção" x "P/ correção").
// Alterar um estado agora é uma edição só, aqui.
//
//   value → o que vai e vem do backend (Activity.Status)
//   label → nome completo, para tooltips e mensagens
//   short → rótulo exibido; precisa caber em uma linha na coluna Progresso
//   icon  → classe do Font Awesome 6 que representa o estado

import { normalizeForSearch } from "@/utils/stringUtils";

export const PROGRESS_OPTIONS = [
  {
    value: "Em andamento",
    label: "Em Andamento",
    short: "Andamento",
    icon: "fa-hourglass-half",
  },
  {
    value: "Concluída para correção",
    label: "Concluída para correção",
    short: "P/ correção",
    icon: "fa-paper-plane",
  },
  {
    value: "Em correção",
    label: "Em correção",
    short: "Corrigindo",
    icon: "fa-pen",
  },
  {
    value: "Corrigida",
    label: "Corrigida",
    short: "Corrigida",
    icon: "fa-check-double",
  },
  {
    value: "Concluída",
    label: "Concluída",
    short: "Concluída",
    icon: "fa-flag-checkered",
  },
];

// Ícone de quem não casa com nenhum estado conhecido (status livre do backend).
export const FALLBACK_PROGRESS_ICON = "fa-bars-progress";

// Estado inicial de toda atividade nova.
export const DEFAULT_PROGRESS = PROGRESS_OPTIONS[0].value;

/**
 * O status chega do backend como texto livre; a comparação normalizada evita
 * depender de caixa e acentuação. Devolve null quando não há correspondência.
 */
export function findProgressOption(value) {
  const target = normalizeForSearch(value);
  if (!target) return null;
  return (
    PROGRESS_OPTIONS.find((o) => normalizeForSearch(o.value) === target) ?? null
  );
}

/** Rótulo curto (chips, badges e opções). Sem correspondência, o valor como veio. */
export function progressShortLabel(value) {
  return findProgressOption(value)?.short ?? (value || "—");
}

/** Nome completo (tooltips e mensagens). Sem correspondência, o valor como veio. */
export function progressFullLabel(value) {
  return findProgressOption(value)?.label ?? (value || "—");
}

/** Ícone do status. Sem correspondência, o ícone genérico de progresso. */
export function progressIcon(value) {
  return findProgressOption(value)?.icon ?? FALLBACK_PROGRESS_ICON;
}

/**
 * Classe de cor do status (badges e opções do dropdown). As regras ficam aqui
 * junto do vocabulário porque a ordem dos testes importa: "concluída para
 * correção" também contém "concluída", e "em correção" contém "correção".
 * Cada tela define no seu <style> o que status-red/orange/blue/green/yellow
 * significam visualmente.
 */
export function progressColorClass(value) {
  const name = value?.toLowerCase() || "";
  if (name.includes("em correção")) return "status-red";
  if (name.includes("concluída para correção")) return "status-orange";
  if (name.includes("concluída")) return "status-blue";
  if (name.includes("corrigida")) return "status-green";
  if (name.includes("em andamento")) return "status-yellow";
  return "";
}

/** Compara dois status ignorando caixa/acentuação (ex.: marcar a opção atual). */
export function isSameProgress(a, b) {
  const na = normalizeForSearch(a);
  return Boolean(na) && na === normalizeForSearch(b);
}

export const PLANNING_PROGRESS = {
  future: {
    label: "Futura",
    short: "Futura",
    colorClass: "status-future",
    icon: "fa-calendar-days",
  },
  today: {
    label: "Início previsto hoje",
    short: "Iniciar hoje",
    colorClass: "status-due-today",
    icon: "fa-calendar-check",
  },
  overdue: {
    label: "Início atrasado",
    short: "Atrasada",
    colorClass: "status-overdue",
    icon: "fa-clock",
  },
};

/**
 * Primeira entrada do menu de progresso: não é um status do fluxo, e sim o
 * MODO da atividade — planejada (ainda não iniciada) x em execução. O value é
 * sentinela local, interceptado pela tela para abrir o formulário de
 * planejamento; nunca chega ao backend.
 */
export const PLANNED_PROGRESS_ACTION = {
  value: "__planned__",
  label: "Planejar datas previstas",
  short: "Planejada",
  icon: "fa-calendar-days",
};

/** Menu completo: o modo primeiro, depois os status do fluxo. */
export const ACTIVITY_PROGRESS_MENU_OPTIONS = [
  PLANNED_PROGRESS_ACTION,
  ...PROGRESS_OPTIONS,
];

/** Classe de cor de uma entrada do menu, seja ela o modo ou um status. */
export function progressMenuColorClass(option) {
  return option.value === PLANNED_PROGRESS_ACTION.value
    ? PLANNING_PROGRESS.future.colorClass
    : progressColorClass(option.value);
}

/** Returns the visual planning state without changing the persisted workflow status. */
export function activityPlanningProgress(activity, now = new Date()) {
  if (!activity?.plannedStartAt || activity?.startedAt) return null;
  const rawPlanned = activity.plannedStartAt;
  const planned = new Date(
    typeof rawPlanned === "string" &&
    !/[zZ]|[+-]\d{2}:?\d{2}$/.test(rawPlanned)
      ? `${rawPlanned}Z`
      : rawPlanned
  );
  if (Number.isNaN(planned.getTime())) return null;

  const saoPauloDay = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const sameLocalDay = saoPauloDay.format(planned) === saoPauloDay.format(now);

  if (sameLocalDay) return PLANNING_PROGRESS.today;
  return planned > now ? PLANNING_PROGRESS.future : PLANNING_PROGRESS.overdue;
}
